/**
 * Sabse badi udaan — biggest impulse purchase, hero card.
 * Editorial spotlight with :has() reactive border + score badge.
 */
import { store } from '../store.ts';
import { formatINR, MERCHANT_DISPLAY } from '../types.ts';
import type { Transaction } from '../types.ts';

export class HisaabImpulseSpotlight extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary?.insights?.biggest) {
        this.#render(s.summary.insights.biggest, s.summary.insights.impulse_score);
      } else {
        this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      }
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(tx: Transaction, impulseScore: number): void {
    const merchant = MERCHANT_DISPLAY[tx.merchant_id] ?? tx.merchant_id;
    const items = tx.items.length ? tx.items.join(', ') : 'item details unavailable';
    const date = formatDate(tx.date);
    const scoreBand = impulseScore >= 75 ? 'high' : impulseScore >= 45 ? 'mid' : 'low';
    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: block;
  view-transition-name: impulse;
}
.card {
  position: relative;
  isolation: isolate;
  border: 1px solid var(--rose);
  border-radius: 12px;
  padding: 30px 24px 32px;
  background: linear-gradient(155deg, oklch(22% 0.09 25 / 0.5), var(--paper-card) 70%);
  overflow: hidden;
  transition: border-color 320ms cubic-bezier(0.32, 0.72, 0, 1),
              box-shadow 320ms cubic-bezier(0.32, 0.72, 0, 1);
}
/* :has() reactive: when score chip is focused, card brightens */
.card:has(.score:focus-visible),
.card:hover {
  border-color: oklch(72% 0.22 25);
  box-shadow: 0 10px 40px -20px oklch(60% 0.2 25 / 0.5);
}
.card::before {
  content: '';
  position: absolute;
  inset: -1px -1px auto -1px;
  height: 80px;
  pointer-events: none;
  background: radial-gradient(ellipse 60% 100% at 50% 0%, oklch(60% 0.2 25 / 0.45), transparent 60%);
  z-index: -1;
}
.label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--rose);
  opacity: 0.95;
}
.amt {
  font-family: var(--display);
  font-size: clamp(54px, 15cqw, 92px);
  font-weight: 700;
  margin-top: 10px;
  line-height: 0.92;
  letter-spacing: -0.025em;
  color: var(--ink);
  font-feature-settings: 'tnum' 1, 'lnum' 1, 'ss01' 1;
  text-wrap: balance;
}
.merchant {
  font-family: var(--display);
  font-size: 26px;
  font-style: italic;
  color: var(--gold);
  margin-top: 12px;
  letter-spacing: -0.005em;
}
.items {
  font-family: var(--display);
  font-style: italic;
  font-size: 16px;
  color: var(--ink-dim);
  margin-top: 14px;
  line-height: 1.5;
  text-wrap: pretty;
}
.foot {
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 16px;
  margin-top: 26px;
  padding-top: 20px;
  border-top: 1px dashed var(--rule);
}
.date {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.score {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--rose);
  text-align: right;
  display: grid;
  gap: 4px;
  cursor: pointer;
  outline: none;
}
.score b {
  font-family: var(--display);
  font-size: 28px;
  font-weight: 700;
  font-style: italic;
  display: block;
  letter-spacing: -0.02em;
  color: var(--ink);
  line-height: 0.95;
  font-feature-settings: 'tnum' 1;
}
.score[data-band="high"] b { color: oklch(80% 0.22 25); }
.score[data-band="mid"]  b { color: var(--gold); }
.score[data-band="low"]  b { color: var(--leaf); }
.score-bar {
  width: 64px;
  height: 2px;
  background: oklch(22% 0.04 280);
  border-radius: 999px;
  overflow: hidden;
  margin-left: auto;
}
.score-bar span {
  display: block;
  height: 100%;
  width: ${Math.min(100, Math.max(0, impulseScore))}%;
  background: linear-gradient(90deg, var(--leaf), var(--gold), var(--rose));
  animation: grow 800ms cubic-bezier(0.32, 0.72, 0, 1) both;
}
@keyframes grow {
  from { transform: scaleX(0); transform-origin: left; }
  to   { transform: scaleX(1); }
}
@media (prefers-reduced-motion: reduce) {
  .score-bar span { animation: none; }
}
</style>
<div class="card">
  <div class="label">Sabse badi udaan</div>
  <div class="amt">${formatINR(tx.amount_inr)}</div>
  <div class="merchant">${merchant}</div>
  <div class="items">${escapeHtml(items)}</div>
  <div class="foot">
    <div class="date">${date}</div>
    <div class="score" data-band="${scoreBand}" tabindex="0">
      <b>${impulseScore.toFixed(0)}</b>
      <span>impulse score</span>
      <div class="score-bar"><span></span></div>
    </div>
  </div>
</div>
`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

customElements.define('hisaab-impulse-spotlight', HisaabImpulseSpotlight);
